/* ============================================================
   Potty Pathway — Potty Readiness quiz · logic + render
   ------------------------------------------------------------
   Native vanilla JS port of the Lovable React app. No deps.
   Scoring/copy mirror lovable-source/src/lib/quiz.ts and
   src/pages/Index.tsx VERBATIM (readiness questions + the
   evaluateReadiness branches). Do not reshape the scoring.

   Flow:  intro → 5 readiness questions → email gate → "check
   your inbox" confirmation. The readiness verdict is delivered
   by EMAIL (via Klaviyo), not shown on-page — the email gate is
   the conversion step, exactly like the original.

   Email submit posts to the existing Supabase Edge Function
   "klaviyo-subscribe", which holds the PRIVATE Klaviyo key
   server-side. The key embedded below is the Supabase *anon*
   (publishable) key — it is designed to ship in the browser and
   is NOT a secret. No private credential is exposed here.
   ============================================================ */

(function () {
  'use strict';

  var root = document.querySelector('#potty-pathway-root');
  if (!root) return;

  /* ---- Klaviyo subscribe via Supabase Edge Function ---------
     To remove the Supabase dependency later, swap this for
     Klaviyo's client-side API (/client/subscriptions + /client/
     events) using the store's public company_id. Keeping the
     edge function for now because it's already wired + tested. */
  var SUPABASE_FN = 'https://tehvbcrogbvwdwuypdjj.supabase.co/functions/v1/klaviyo-subscribe';
  var SUPABASE_ANON = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRlaHZiY3JvZ2J2d2R3dXlwZGpqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzgxNjY1NzksImV4cCI6MjA5Mzc0MjU3OX0.cTqum6Wf-eTXbxylsiYT6CsoJfuoO2ooYfawhTtAS1k';

  function subscribe(email, readiness) {
    return fetch(SUPABASE_FN, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_ANON,
        'Authorization': 'Bearer ' + SUPABASE_ANON
      },
      body: JSON.stringify({
        email: email,
        readiness: readiness.readiness,
        readinessTitle: readiness.readinessTitle
      })
    });
  }

  /* ------------------------------------------------------------
     Quiz config — the 5 readiness questions (verbatim).
     Each choice carries the score used by evaluateReadiness().
     ------------------------------------------------------------ */
  var questions = [
    {
      id: 'diaper',
      text: 'Is your little one bothered by a wet or dirty diaper?',
      choices: [
        { label: 'Yes, often', value: 2 },
        { label: 'Starting to be', value: 1 },
        { label: "No, they don't seem to notice", value: 0 }
      ]
    },
    {
      id: 'curious',
      text: 'Is your child curious about the potty?',
      hint: 'e.g. following you into the bathroom, asking questions, wanting to sit on the potty',
      choices: [
        { label: 'Yes, all the time', value: 2 },
        { label: 'Occasionally', value: 1 },
        { label: 'Not at all', value: 0 }
      ]
    },
    {
      id: 'dry',
      text: 'Can your child go two hours or more with a dry diaper?',
      choices: [
        { label: 'Yes, most of the time', value: 2 },
        { label: 'Sometimes', value: 1 },
        { label: 'No, not yet', value: 0 }
      ]
    },
    {
      id: 'communicate',
      text: 'Does your child give you a heads up before they need to go (using words or gestures)?',
      choices: [
        { label: 'Yes, quite often', value: 2 },
        { label: 'Every now and then', value: 1 },
        { label: 'Not at this point', value: 0 }
      ]
    },
    {
      id: 'parent',
      text: 'Do you feel ready to take on a potty training routine right now?',
      hint: "Be honest — this matters as much as your child's readiness!",
      choices: [
        { label: "Yes, I'm so ready", value: 4 },
        { label: "I'm open to it, but not sure", value: 2 },
        { label: 'Honestly not ready to juggle this yet', value: 0 }
      ]
    }
  ];

  /* ------------------------------------------------------------
     evaluateReadiness — VERBATIM port of quiz.ts. Same branches,
     same thresholds, same copy. DO NOT REWRITE.
     ------------------------------------------------------------ */
  function evaluateReadiness(answers) {
    var signKeys = ['diaper', 'curious', 'dry', 'communicate'];
    var signScore = signKeys.reduce(function (s, k) { return s + (answers[k] != null ? answers[k] : 0); }, 0);
    var parentScore = answers.parent != null ? answers.parent : 0;
    var total = signScore + parentScore;

    if (parentScore === 0) {
      return {
        readiness: 'not-yet',
        readinessTitle: "Hold tight, mama — now's not the moment",
        readinessBody:
          "Your little one may be showing some signs, but potty training takes a lot of you too — and that's okay! It works best when you're truly ready to commit. Wait until life feels a little calmer, then revisit this quiz. No rush."
      };
    }
    if (total < 6) {
      return {
        readiness: 'not-yet',
        readinessTitle: "Not quite ready — and that's totally normal!",
        readinessBody:
          "Your kiddo is still building the skills they need. Pushing too early usually backfires. Keep modeling potty habits, read a few potty books together, and check back in a month or two — they'll get there."
      };
    }
    if (total < 9) {
      return {
        readiness: 'almost',
        readinessTitle: "So close — you're almost there!",
        readinessBody:
          'Your child is showing several signs of readiness. You can wait a bit longer or start a low-pressure introduction, like getting them familiar with the potty. A gentle ramp-up now can set you up for a smoother start.'
      };
    }
    return {
      readiness: 'ready',
      readinessTitle: "You're ready to go for it!",
      readinessBody:
        "Lots of green flags here — and you're feeling ready too. Pick a low-stress week, stock up on wipes and patience, and dive in. Most kiddos with this many signs train within 1–3 months."
    };
  }

  /* ------------------------------------------------------------
     Tiny DOM helper:  h('div', {class:'foo'}, child, child, …)
     ------------------------------------------------------------ */
  function h(tag, attrs) {
    var el = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (k === 'class') el.className = v;
        else if (k === 'html') el.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
        else if (v === false || v == null) continue;
        else if (v === true) el.setAttribute(k, '');
        else el.setAttribute(k, v);
      }
    }
    for (var i = 2; i < arguments.length; i++) {
      append(el, arguments[i]);
    }
    return el;
  }
  function append(el, c) {
    if (c == null || c === false) return;
    if (Array.isArray(c)) { c.forEach(function (cc) { append(el, cc); }); return; }
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }

  function svg(inner) {
    var wrap = document.createElement('span');
    wrap.innerHTML = '<svg class="pp-icon" viewBox="0 0 24 24" aria-hidden="true">' + inner + '</svg>';
    return wrap.firstChild;
  }
  function iconArrow() { return svg('<path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>'); }
  function iconRotate() { return svg('<path d="M3 12a9 9 0 1 1 3 6.7M3 21v-5h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>'); }
  function iconMail() { return svg('<path d="M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M3.5 6.5l8.5 6 8.5-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>'); }

  /* ------------------------------------------------------------
     State + render loop
     ------------------------------------------------------------ */
  var state = {
    stage: 'intro',   // intro | quiz | email | result
    step: 0,
    answers: {},
    readiness: null,
    submitting: false
  };

  function resetAll() {
    state.stage = 'intro'; state.step = 0; state.answers = {};
    state.readiness = null; state.submitting = false;
    render(); scrollIntoQuiz();
  }

  // Restart link shown top-right on every screen except the intro
  // (mirrors the Lovable header "Restart").
  function renderTopbar() {
    return h('div', { class: 'pp-topbar' },
      h('button', { class: 'pp-restart', type: 'button', onclick: resetAll },
        iconRotate(), 'Restart')
    );
  }

  function render() {
    root.innerHTML = '';
    if (state.stage !== 'intro') root.appendChild(renderTopbar());
    if (state.stage === 'intro') root.appendChild(renderIntro());
    else if (state.stage === 'quiz') root.appendChild(renderQuestion());
    else if (state.stage === 'email') root.appendChild(renderEmail());
    else root.appendChild(renderResult());
  }

  function scrollIntoQuiz() {
    var rect = root.getBoundingClientRect();
    if (rect.top < 0) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---- Intro ---- */
  function renderIntro() {
    return h('div', { class: 'pp-card pp-intro' },
      h('p', { class: 'pp-eyebrow' }, 'Potty Training Readiness Quiz'),
      h('h1', { class: 'pp-intro__title' },
        'Is your kid ',
        h('span', { class: 'pp-accent' }, 'actually ready'),
        ' to start potty training?'
      ),
      h('p', { class: 'pp-intro__sub' },
        "Take our quick 1-minute quiz to find out (plus, some guidance on how to make the transition easier)."
      ),
      h('p', { class: 'pp-intro__note' }, 'Results sent straight to your inbox.'),
      h('div', { class: 'pp-intro__cta' },
        h('button', {
          class: 'pp-btn pp-btn--primary pp-btn--lg',
          type: 'button',
          onclick: function () { state.stage = 'quiz'; state.step = 0; render(); }
        }, 'Start the quiz ', iconArrow())
      )
    );
  }

  /* ---- Question ---- */
  function renderQuestion() {
    var q = questions[state.step];
    var total = questions.length;
    var num = state.step + 1;
    var pct = Math.round((num / total) * 100);

    var card = h('div', { class: 'pp-card' },
      h('div', { class: 'pp-progress' },
        h('div', { class: 'pp-progress__meta' },
          h('span', null, 'Readiness check · ' + num + ' of ' + total),
          h('span', null, pct + '%')
        ),
        progressBar(num, total)
      ),
      h('div', { class: 'pp-question__head' },
        h('h2', { class: 'pp-question__title' }, q.text),
        q.hint ? h('p', { class: 'pp-question__hint' }, q.hint) : null
      ),
      h('div', { class: 'pp-choices' },
        q.choices.map(function (c) {
          return h('button', {
            class: 'pp-choice' + (state.answers[q.id] === c.value ? ' is-selected' : ''),
            type: 'button',
            onclick: function () { select(q, c.value); }
          },
            h('span', { class: 'pp-choice__label' }, c.label),
            h('span', { class: 'pp-choice__arrow' }, iconArrow())
          );
        })
      ),
      state.step > 0
        ? h('button', {
            class: 'pp-back',
            type: 'button',
            onclick: function () { state.step = Math.max(0, state.step - 1); render(); }
          }, '← Back')
        : null
    );
    return card;
  }

  function select(q, value) {
    state.answers[q.id] = value;
    if (state.step + 1 < questions.length) {
      state.step += 1;
    } else {
      state.readiness = evaluateReadiness(state.answers);
      state.stage = 'email';
    }
    render();
    scrollIntoQuiz();
  }

  // Segmented progress bar — inline SVG so theme CSS can't touch the
  // fills (one segment per question; same treatment as Potty Finder).
  function progressBar(filledSegs, total) {
    var W = 600, H = 8;
    var fillW = (filledSegs / total) * W;
    var dividers = '';
    for (var i = 1; i < total; i++) {
      var x = (i / total) * W;
      dividers += '<rect x="' + (x - 1) + '" y="0" width="2" height="' + H + '" fill="#ffffff"/>';
    }
    var bar = document.createElement('div');
    bar.className = 'pp-progress__svg';
    bar.innerHTML =
      '<svg width="100%" height="' + H + '" viewBox="0 0 ' + W + ' ' + H +
      '" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" style="display:block">' +
        '<rect width="' + W + '" height="' + H + '" rx="4" fill="#dde4ee"/>' +
        '<rect width="' + fillW + '" height="' + H + '" rx="4" fill="#285fa0"/>' +
        dividers +
      '</svg>';
    return bar;
  }

  /* ---- Email gate ---- */
  function renderEmail() {
    var input = h('input', {
      class: 'pp-input',
      type: 'email',
      required: true,
      placeholder: 'you@email.com',
      autocomplete: 'email',
      'aria-label': 'Your email address'
    });
    var btn = h('button', {
      class: 'pp-btn pp-btn--primary pp-btn--block',
      type: 'submit'
    }, state.submitting ? 'Unlocking…' : 'Show me my results ', state.submitting ? null : iconArrow());
    if (state.submitting) btn.setAttribute('disabled', '');

    var form = h('form', {
      class: 'pp-form',
      onsubmit: function (e) {
        e.preventDefault();
        var email = (input.value || '').trim();
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) { input.focus(); return; }
        if (state.submitting) return;
        state.submitting = true;
        render();
        // Fire-and-advance: subscribe in the background, always show the
        // confirmation (mirrors the original — never block the user).
        subscribe(email, state.readiness)
          .catch(function () { /* silently ignore — still confirm */ })
          .then(function () { state.submitting = false; state.stage = 'result'; render(); scrollIntoQuiz(); });
      }
    }, input, btn);

    return h('div', { class: 'pp-card pp-email' },
      h('p', { class: 'pp-email__title' },
        "Done! Where should we send your child's personalized readiness report?"
      ),
      h('p', { class: 'pp-email__sub' },
        'Enter your email to get your detailed results, plus expert tips on what you can do ',
        h('em', null, 'now'),
        ' to prepare for potty training.'
      ),
      form,
      h('p', { class: 'pp-email__consent' },
        'By entering your email, you consent to receive marketing emails from Jool Baby. You can unsubscribe anytime.'
      )
    );
  }

  /* ---- Result (confirmation — verdict is emailed) ---- */
  function renderResult() {
    return h('div', { class: 'pp-card pp-result' },
      h('div', { class: 'pp-result__icon' }, iconMail()),
      h('h2', { class: 'pp-result__title' }, 'Check your inbox!'),
      h('p', { class: 'pp-result__sub' },
        "We've sent a copy of your results to your email, along with next steps tailored to where you are in the journey."
      ),
      h('div', { class: 'pp-result__restart' },
        h('button', {
          class: 'pp-btn pp-btn--outline',
          type: 'button',
          onclick: resetAll
        }, iconRotate(), 'Take the quiz again')
      )
    );
  }

  render();
})();
